import type { ResolvedFigure } from '../model.js';
import { box, point, SceneBuilder, validateViewport } from '../scene-builder.js';
import { finishPrimitive, type PrimitiveSpec } from './contract.js';
import { clamp, expand, fits, frameFor, header, leader, note, textWidth, TYPE } from './kit.js';

export interface HierarchyRank {
  /** Stable id, unique within the figure. */
  id: string;
  /** The name of the level itself — ORDER, DEPARTMENT, TIER. */
  rank: string;
  /** The member occupying that level on this path. */
  label: string;
  /** One factual clause about the member. */
  detail?: string;
  /** A real sibling at the same rank that this path leaves behind. */
  excludes?: string;
}

export interface HierarchySpec extends PrimitiveSpec {
  family: 'primitive-hierarchy';
  view: 'schematic';
  /** What the innermost member is, in the reader's words. */
  subject: string;
  title: string;
  subtitle: string;
  /** Ordered outermost → innermost. Every rank contains the next, wholly. */
  ranks: HierarchyRank[];
  focusRankId?: string;
}

export const hierarchyRankId = (id: string): string => `hierarchy.rank.${id}`;
export const hierarchyExclusionId = (id: string): string => `hierarchy.exclusion.${id}`;

/**
 * Containment, drawn as containment.
 *
 * A hierarchy is not a graph of boxes joined by lines: it is a chain of nested
 * sets in which every member of a level lies wholly inside exactly one member
 * of the level above, and every step inward discards all of that level's other
 * members. This compiler draws the nesting literally — each rank is a container
 * that encloses every deeper rank — and names, for as many ranks as the density
 * tier allows, the real sibling the path excluded. Nothing else in the pack is
 * true of a hierarchy and only of a hierarchy.
 */
export function compileHierarchy(spec: HierarchySpec): ResolvedFigure {
  validateHierarchy(spec);

  const builder = new SceneBuilder();
  const frame = frameFor(spec.viewport.width, spec.viewport.height);
  const ranks = spec.ranks;
  const depth = ranks.length;
  const focusId = spec.focusRankId ?? ranks[depth - 1]!.id;
  const warnings: string[] = [];

  const calloutBudget = frame.tier === 'wide' ? 3 : frame.tier === 'phone' ? 2 : 1;
  const stepX = frame.tier === 'wide' ? 15 : frame.tier === 'phone' ? 11 : 8;
  const stepIn = 6;
  const rowH = frame.tier === 'wide' ? 36 : frame.tier === 'phone' ? 33 : 30;
  // Only the wide tier can afford a side lane; narrower tiers state the same
  // exclusions under the nest instead of shrinking the nest to make room.
  const laneW = frame.tier === 'wide' ? 132 : 0;
  const railGutter = 22;

  const nestLeft = frame.margin + railGutter;
  const nestRight = frame.width - frame.margin - laneW;
  const nestTop = frame.top;
  const nestBottom = nestTop + depth * rowH + (depth - 1) * stepIn;

  header(
    builder,
    frame,
    'hierarchy',
    `NESTED RANKS · ${spec.subject.toUpperCase()}`,
    [
      `${depth} LEVELS · EACH SET LIES WHOLLY INSIDE THE ONE ABOVE`,
      `${depth} LEVELS · EACH SET INSIDE THE ONE ABOVE`,
      `${depth} NESTED LEVELS`,
    ],
  );

  // Which ranks may carry an exclusion note: the deepest ones that declare a
  // real sibling, capped by the density tier's callout budget.
  const exclusionIds = new Set(
    ranks
      .filter((rank) => rank.excludes)
      .slice(-calloutBudget)
      .map((rank) => rank.id),
  );
  const suppressed = ranks.filter((rank) => rank.excludes && !exclusionIds.has(rank.id)).length;
  if (suppressed > 0) {
    warnings.push(`${suppressed} sibling exclusions are held back at this density tier; the drawn nesting is unchanged.`);
  }

  // Every container starts one row below its parent and ends one step above its
  // parent's floor, so the drawing is literal containment: rank i+1 is inside i.
  let belowNotes = 0;
  const bounds = ranks.map((_, index) =>
    box(
      nestLeft + index * stepX,
      nestTop + index * rowH,
      nestRight - nestLeft - index * (stepX + stepIn),
      nestBottom - index * stepIn - (nestTop + index * rowH),
    ),
  );

  ranks.forEach((rank, index) => {
    const semanticId = hierarchyRankId(rank.id);
    const shell = bounds[index]!;
    const innermost = index === depth - 1;
    const active = rank.id === focusId;
    builder.rect(`${semanticId}.container`, semanticId, active ? 'active' : innermost ? 'surface' : 'structure', shell, {
      radius: 4,
      label: `${rank.rank}: ${rank.label}`,
      interactive: true,
    });

    const textLeft = shell.x + 10;
    const baseline = shell.y + rowH * 0.62;
    const kickerWidth = textWidth(rank.rank.toUpperCase(), TYPE.micro, true, true);
    builder.text(`${semanticId}.rank`, semanticId, 'annotation', point(textLeft, baseline), rank.rank.toUpperCase(), {
      size: TYPE.micro,
      mono: true,
      weight: 700,
      uppercase: true,
    });
    const nameX = textLeft + kickerWidth + 10;
    builder.text(`${semanticId}.label`, semanticId, 'structure', point(nameX, baseline), rank.label, {
      size: TYPE.label,
      mono: true,
      weight: 700,
    });

    // The detail clause only appears when it fits beside the name at full size.
    const nameWidth = textWidth(rank.label, TYPE.label, true);
    const detailX = nameX + nameWidth + 12;
    const detailRoom = shell.x + shell.width - 10 - detailX;
    if (rank.detail && fits(rank.detail, TYPE.micro, detailRoom, true)) {
      builder.text(`${semanticId}.detail`, semanticId, 'annotation', point(detailX, baseline), rank.detail, {
        size: TYPE.micro,
        mono: true,
        weight: 500,
      });
    }

    builder.anchor(`${semanticId}.corner`, semanticId, point(shell.x, shell.y), 'left');
    builder.anchor(`${semanticId}.name`, semanticId, point(nameX, baseline - 4), 'right');
    builder.hit(semanticId, box(shell.x, shell.y, shell.width, rowH));

    if (index > 0) {
      const parent = ranks[index - 1]!;
      builder.relation(
        `${semanticId}.within`,
        'contained-by',
        semanticId,
        hierarchyRankId(parent.id),
        `every ${rank.rank.toLowerCase()} ${rank.label} lies inside ${parent.label}`,
      );
    }

    if (rank.excludes && exclusionIds.has(rank.id)) {
      const exclusionSemantic = hierarchyExclusionId(rank.id);
      const text = `≠ ${rank.excludes}`;
      if (laneW > 0) {
        const laneX = frame.width - frame.margin - laneW;
        const anchorY = shell.y + rowH / 2;
        leader(builder, `${exclusionSemantic}.leader`, exclusionSemantic, point(shell.x + shell.width, anchorY), point(laneX + 8, anchorY), laneX - 12);
        builder.text(`${exclusionSemantic}.label`, exclusionSemantic, 'annotation', point(laneX + 12, anchorY + 4), text, {
          size: TYPE.micro,
          mono: true,
          weight: 700,
        });
        builder.anchor(`${exclusionSemantic}.tip`, exclusionSemantic, point(laneX + 8, anchorY), 'left');
        builder.hit(exclusionSemantic, box(laneX, anchorY - 12, laneW, 24));
      } else {
        // Narrow tiers: the same exclusions, stated under the nest in full.
        const y = nestBottom + 24 + belowNotes * 18;
        belowNotes += 1;
        builder.text(`${exclusionSemantic}.label`, exclusionSemantic, 'annotation', point(frame.margin, y), `${rank.rank.toUpperCase()} EXCLUDES ${rank.excludes.toUpperCase()}`, {
          size: TYPE.micro,
          mono: true,
          weight: 700,
          uppercase: true,
        });
        builder.anchor(`${exclusionSemantic}.tip`, exclusionSemantic, point(frame.margin, y - 4), 'up');
        builder.hit(exclusionSemantic, box(frame.margin, y - 14, frame.contentWidth, 22));
      }
      builder.relation(
        `${exclusionSemantic}.excludes`,
        'excludes',
        hierarchyRankId(rank.id),
        exclusionSemantic,
        `choosing ${rank.label} at ${rank.rank.toLowerCase()} excludes ${rank.excludes}`,
      );
    }
  });

  // The depth measure: how many nested decisions separate the subject from the root.
  const railX = clamp(nestLeft - 8, 6, frame.width);
  builder.line('hierarchy.depth.rail', 'hierarchy.depth', 'dimension', point(railX, nestTop), point(railX, nestBottom), { markerStart: true, markerEnd: true });
  builder.text('hierarchy.depth.label', 'hierarchy.depth', 'annotation', point(railX - 6, (nestTop + nestBottom) / 2), `${depth - 1} STEPS IN`, {
    size: TYPE.micro,
    mono: true,
    weight: 700,
    align: 'middle',
    rotate: -90,
    uppercase: true,
  });
  builder.anchor('hierarchy.depth.top', 'hierarchy.depth', point(railX, nestTop), 'up');
  builder.anchor('hierarchy.depth.bottom', 'hierarchy.depth', point(railX, nestBottom), 'down');
  builder.hit('hierarchy.depth', box(0, nestTop, railX + 8, nestBottom - nestTop));

  const footerY = Math.min(frame.bottom, nestBottom + (belowNotes > 0 ? 24 + belowNotes * 18 + 8 : 28));
  note(builder, 'hierarchy.law', 'hierarchy', point(frame.margin, footerY), 'ONE PARENT EACH · NO MEMBER APPEARS TWICE');

  return finishPrimitive(spec, builder, {
    title: spec.title,
    subtitle: spec.subtitle,
    state: spec.focusRankId ? `focus:${spec.focusRankId}` : 'overview',
    active: [hierarchyRankId(focusId), ...(spec.emphasis ?? [])],
    warnings,
  });
}

export function validateHierarchy(spec: HierarchySpec): void {
  validateViewport(spec.viewport.width, spec.viewport.height);
  if (spec.family !== 'primitive-hierarchy' || spec.view !== 'schematic') throw new Error('hierarchy: family and view are fixed');
  if (!spec.provenance.trim()) throw new Error('hierarchy: provenance is required');
  if (!spec.subject.trim() || !spec.title.trim() || !spec.subtitle.trim()) throw new Error('hierarchy: subject, title and subtitle are required');
  if (spec.ranks.length < 2 || spec.ranks.length > 8) throw new Error('hierarchy: require 2…8 nested ranks');
  const ids = new Set<string>();
  for (const rank of spec.ranks) {
    if (!/^[a-z][a-z0-9-]*$/.test(rank.id)) throw new Error(`hierarchy: invalid rank id ${rank.id}`);
    if (ids.has(rank.id)) throw new Error(`hierarchy: duplicate rank id ${rank.id}`);
    ids.add(rank.id);
    if (!rank.rank.trim() || !rank.label.trim()) throw new Error(`hierarchy: rank ${rank.id} requires a level name and a member`);
    if (rank.rank.length > 12) throw new Error(`hierarchy: rank name ${rank.rank} exceeds 12 characters`);
    if (rank.label.length > 26) throw new Error(`hierarchy: member ${rank.label} exceeds 26 characters`);
    if (rank.excludes && rank.excludes.length > 22) throw new Error(`hierarchy: sibling ${rank.excludes} exceeds 22 characters`);
  }
  if (spec.focusRankId && !ids.has(spec.focusRankId)) throw new Error(`hierarchy: focus rank ${spec.focusRankId} does not exist`);
  const required = 74 + spec.ranks.length * 30 + (spec.ranks.length - 1) * 6 + 84;
  if (spec.viewport.height < required) throw new Error(`hierarchy: ${spec.ranks.length} ranks need at least ${required}px of height`);
}

export function hierarchyLeaderBounds(bounds: ReturnType<typeof box>): ReturnType<typeof box> {
  return expand(bounds, 6);
}
